/**
 * Edit Voice Form
 *
 * Form for editing voice metadata in the database.
 * Read-only provider fields, editable description/languages/gender/status.
 *
 * Acceptance Criteria: AC 30, 34
 */

import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  useUpdateVoice,
  useGenerateVoiceSample,
  useProviderVoices,
  type VoiceGender,
  type VoiceResponse,
} from '../services/voiceApi';
import { useInitialData } from 'contexts/InitialDataContext';
import { useLocales } from 'features/dictionaries/services/localesApi';

interface EditVoiceFormProps {
  isOpen: boolean;
  onClose: () => void;
  voice: VoiceResponse;
}

export function EditVoiceForm({ isOpen, onClose, voice }: EditVoiceFormProps) {
  const { state: initialDataState } = useInitialData();

  // Fetch provider voices to get available languages from TTS
  const { data: providerVoices } = useProviderVoices(isOpen ? voice.providerId : undefined);

  // Fetch available locales for locale assignment
  const { data: allLocales } = useLocales();

  // Get available languages from TTS provider for this specific voice
  const ttsAvailableLanguages = useMemo(() => {
    if (!providerVoices) return [];
    const providerVoice = providerVoices.find((v) => v.providerVoiceId === voice.providerVoiceId);
    return providerVoice?.languages || [];
  }, [providerVoices, voice.providerVoiceId]);

  // Get language options from initial data, filtered by TTS available languages
  const languageOptions = useMemo(() => {
    if (!initialDataState.data?.languages) return [];
    // Only show languages that are available from the TTS provider
    return initialDataState.data.languages
      .filter((lang) => ttsAvailableLanguages.includes(lang.code))
      .map((lang) => ({
        code: lang.code,
        name: lang.name,
      }));
  }, [initialDataState.data?.languages, ttsAvailableLanguages]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [gender, setGender] = useState<VoiceGender>('unclear');
  const [localeCode, setLocaleCode] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

  // Filter locales to only show those matching selected languages
  const availableLocales = useMemo(() => {
    if (!allLocales || selectedLanguages.length === 0) return [];
    return allLocales.filter((locale) => selectedLanguages.includes(locale.languageCode));
  }, [allLocales, selectedLanguages]);

  const updateVoiceMutation = useUpdateVoice();
  const generateSampleMutation = useGenerateVoiceSample();

  const descriptionMinLength = 10;
  const descriptionMaxLength = 1000;
  const nameMaxLength = 100;

  // Initialize form with voice data
  useEffect(() => {
    if (isOpen && voice) {
      setName(voice.name || '');
      setDescription(voice.description || '');
      setSelectedLanguages(voice.languageCodes || []);
      setGender(voice.gender || 'unclear');
      setLocaleCode(voice.localeCode || null);
      setIsActive(voice.isActive !== undefined ? voice.isActive : true);
      setHasUnsavedChanges(false);
      setAudioUrl(null);
      setErrors({});
    }
  }, [isOpen, voice]);

  // Track changes
  useEffect(() => {
    if (voice) {
      const hasChanges =
        name !== voice.name ||
        description !== voice.description ||
        JSON.stringify(selectedLanguages) !== JSON.stringify(voice.languageCodes) ||
        gender !== voice.gender ||
        localeCode !== (voice.localeCode || null) ||
        isActive !== voice.isActive;
      setHasUnsavedChanges(hasChanges);
    }
  }, [name, description, selectedLanguages, gender, localeCode, isActive, voice]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Voice name is required';
    } else if (name.length > nameMaxLength) {
      newErrors.name = `Voice name must be less than ${nameMaxLength} characters`;
    }

    if (description.length < descriptionMinLength) {
      newErrors.description = `Description must be at least ${descriptionMinLength} characters`;
    } else if (description.length > descriptionMaxLength) {
      newErrors.description = `Description must be no more than ${descriptionMaxLength} characters`;
    }

    if (selectedLanguages.length === 0) {
      newErrors.languages = 'At least one language is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddLanguage = (languageCode: string) => {
    if (!selectedLanguages.includes(languageCode)) {
      setSelectedLanguages([...selectedLanguages, languageCode]);
    }
  };

  const handleRemoveLanguage = (languageCode: string) => {
    setSelectedLanguages(selectedLanguages.filter((code) => code !== languageCode));
  };

  const handleVoicePreview = async () => {
    try {
      const response = await generateSampleMutation.mutateAsync({
        voiceId: voice.id,
        text: 'This is a preview of the voice. Hello, how are you today?',
      });
      setAudioUrl(response.audioUrl);
    } catch {
      toast.error('Failed to generate voice preview');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await updateVoiceMutation.mutateAsync({
        voiceId: voice.id,
        update: {
          name: name.trim(),
          description: description.trim(),
          languageCodes: selectedLanguages,
          gender,
          localeCode: localeCode,
          isActive,
        },
      });

      toast.success(`Voice "${name}" updated successfully!`);
      onClose();
    } catch (err) {
      setErrors({
        submit: err instanceof Error ? err.message : 'Failed to update voice',
      });
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setShowUnsavedConfirm(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="edit-voice-form"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold" data-testid="form-header">
            Edit Voice
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
            data-testid="close-button"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Read-only fields */}
          <div className="mb-6 flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Provider: {voice.providerName}
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              ID: {voice.providerVoiceId}
            </span>
          </div>

          {/* Voice Name */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Voice Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={nameMaxLength}
              className={`w-full px-3 py-2 border ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter voice name"
              data-testid="name-input"
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">Max {nameMaxLength} characters</span>
              <span
                className={`text-xs ${
                  name.length > nameMaxLength ? 'text-red-600' : 'text-gray-500'
                }`}
                data-testid="name-counter"
              >
                {name.length} / {nameMaxLength}
              </span>
            </div>
            {errors.name && (
              <p className="text-red-500 text-xs mt-1" data-testid="name-error">
                {errors.name}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className={`w-full px-3 py-2 border ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              data-testid="description-input"
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">
                {descriptionMinLength}-{descriptionMaxLength} characters required
              </span>
              <span
                className={`text-xs ${
                  description.length < descriptionMinLength ||
                  description.length > descriptionMaxLength
                    ? 'text-red-600'
                    : 'text-gray-500'
                }`}
                data-testid="description-counter"
              >
                {description.length} / {descriptionMaxLength}
              </span>
            </div>
            {errors.description && (
              <p className="text-red-500 text-xs mt-1" data-testid="description-error">
                {errors.description}
              </p>
            )}
          </div>

          {/* Languages */}
          <div className="mb-4">
            <label
              htmlFor="language-select"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Languages <span className="text-red-500">*</span>
            </label>
            <select
              id="language-select"
              onChange={(e) => {
                if (e.target.value) {
                  handleAddLanguage(e.target.value);
                  e.target.value = '';
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
              data-testid="language-select"
            >
              <option value="">Select a language...</option>
              {languageOptions.map((lang) => (
                <option
                  key={lang.code}
                  value={lang.code}
                  disabled={selectedLanguages.includes(lang.code)}
                >
                  {lang.name}
                </option>
              ))}
            </select>

            <div className="flex flex-wrap gap-2" data-testid="selected-languages">
              {selectedLanguages.map((code) => {
                const lang = languageOptions.find((l) => l.code === code);
                return (
                  <span
                    key={code}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center"
                    data-testid={`language-badge-${code}`}
                  >
                    {lang?.name || code}
                    <button
                      type="button"
                      onClick={() => handleRemoveLanguage(code)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                      data-testid={`remove-language-${code}`}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
            {errors.languages && (
              <p className="text-red-500 text-xs mt-1" data-testid="languages-error">
                {errors.languages}
              </p>
            )}
          </div>

          {/* Gender */}
          <div className="mb-4">
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value as VoiceGender)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="gender-select"
            >
              <option value="unclear">Unclear</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          {/* Locale (Regional Accent) */}
          <div className="mb-4">
            <label htmlFor="locale" className="block text-sm font-medium text-gray-700 mb-2">
              Regional Accent (Locale)
            </label>
            <select
              id="locale"
              value={localeCode || ''}
              onChange={(e) => setLocaleCode(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="locale-select"
              disabled={selectedLanguages.length === 0}
            >
              <option value="">-- No specific accent --</option>
              {availableLocales.map((locale) => (
                <option key={locale.localeCode} value={locale.localeCode}>
                  {locale.name} ({locale.localeCode})
                </option>
              ))}
            </select>
            {selectedLanguages.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Select at least one language to assign a regional accent
              </p>
            )}
            {selectedLanguages.length > 0 && availableLocales.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No locales available for the selected language(s)
              </p>
            )}
          </div>

          {/* Status */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="mr-2"
                data-testid="status-toggle"
              />
              <span className="text-sm font-medium text-gray-700">Active</span>
            </label>
          </div>

          {/* Voice Preview */}
          <div className="mb-6">
            <button
              type="button"
              onClick={handleVoicePreview}
              disabled={generateSampleMutation.isPending}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-green-400"
              data-testid="preview-button"
            >
              {generateSampleMutation.isPending ? 'Generating Preview...' : 'Voice Preview'}
            </button>
            {audioUrl && (
              <audio controls className="w-full mt-3" data-testid="preview-audio">
                <source src={audioUrl} type="audio/mpeg" />
              </audio>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div
              className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded"
              data-testid="submit-error"
            >
              {errors.submit}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              data-testid="cancel-button"
              disabled={updateVoiceMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
              data-testid="save-button"
              disabled={updateVoiceMutation.isPending || !hasUnsavedChanges}
            >
              {updateVoiceMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>

      {/* Unsaved Changes Confirmation Dialog */}
      {showUnsavedConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Unsaved Changes</h3>
            <p className="text-gray-600 mb-6">
              You have unsaved changes. Are you sure you want to close?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowUnsavedConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="cancel-unsaved-button"
              >
                Cancel
              </button>
              <button
                onClick={confirmClose}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                data-testid="confirm-unsaved-button"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
